<template>
    <Accordion
        :is-open
        class="filter-accordion"
        @toggle="toggle"
    >
        <template #title>
            <div
                ref="titleEl"
                class="title"
            >
                {{ title }}

                <span
                    v-if="count"
                    class="count"
                >
                    &nbsp;{{ hideCount ? '•' : `(${count})` }}
                </span>

                <Icon
                    class="icon"
                    icon="angle-down"
                />
            </div>
        </template>

        <template #default>
            <slot :disabled="!isOpen"></slot>
        </template>
    </Accordion>
</template>

<script lang="ts" setup>
import { nextTick, ref, useTemplateRef, watch } from 'vue'

import Icon from '@/components/ui/icon/Icon.vue'
import Accordion from '@/components/layout/Accordion.vue'

const {
    hideCount = false,
    isOpenAtStart = false,
    count,
    shouldFocus = undefined,
} = defineProps<{
    title: string
    count: number | undefined
    hideCount?: boolean
    isOpenAtStart?: boolean
    // Focus the first accordion when the modal opens
    shouldFocus?: {
        flag: boolean
    }
}>()

defineSlots<{
    default(props: { disabled: boolean }): any
}>()

if (shouldFocus) {
    const titleEl = useTemplateRef('titleEl')

    watch(
        () => shouldFocus.flag,
        (flag) => {
            if (flag) {
                nextTick(() => {
                    titleEl.value?.focus()
                })
            }
        },
        { immediate: true }
    )
}

const isOpen = ref(isOpenAtStart)
const toggle = () => {
    isOpen.value = !isOpen.value
}

watch(
    () => count,
    (c) => {
        if (c) {
            isOpen.value = true
        }
    },
    { immediate: true }
)
</script>

<style lang="scss" scoped>
.filter-accordion {
    --time-open: 0.2s;
    --time-close: 0.12s;
    border-bottom: 1px solid var(--grey-200);
    position: relative;

    .title {
        height: 60px;
        display: flex;
        align-items: center;
        padding: 0 10px;
        user-select: none;
        cursor: pointer;

        font-family: $effra-font-stack;
        font-size: 14px;
        text-transform: uppercase;

        font-weight: 400;
        line-height: 15px;
        letter-spacing: 0.05em;
        text-align: center;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;

        .icon {
            font-size: 1rem;
            margin-left: auto;
            display: block;
            transition: transform var(--time-close)
                var(--timing-function-easing-emphasized);
        }
    }

    ::v-deep(.content) {
        height: 0;
        padding: 0 2px;
        opacity: 0;
        transition: all var(--time-close)
            var(--timing-function-easing-emphasized);
        overflow: hidden;

        .cw {
            padding: 10px 0 20px 0;
        }
    }

    &.open {
        .icon {
            transform: rotateZ(180deg);
            transition: transform var(--time-open)
                var(--timing-function-easing-emphasized);
        }
    }
}
</style>
